/**
 * Converts HTML from rich-text editors (e.g. ReactQuill) to plain text for previews,
 * tables, and anywhere `dangerouslySetInnerHTML` is not appropriate.
 */
export function htmlToPlainText(html: string): string {
  const trimmed = html?.trim();
  if (!trimmed) {
    return "";
  }

  if (typeof document !== "undefined") {
    const doc = new DOMParser().parseFromString(trimmed, "text/html");
    const text = doc.body.textContent ?? "";
    return text.replace(/\s+/g, " ").trim();
  }

  return trimmed
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const RICH_TEXT_TAGS = new Set([
  "A",
  "B",
  "BR",
  "EM",
  "I",
  "LI",
  "OL",
  "P",
  "S",
  "STRONG",
  "U",
  "UL",
]);

/** Restricts public-profile rich text to the formats exposed by its editor toolbar. */
export function sanitizeRichTextHtml(html: string): string {
  if (typeof document === "undefined") return "";

  const parsed = new DOMParser().parseFromString(html, "text/html");
  const elements = Array.from(parsed.body.querySelectorAll("*"));

  elements.forEach((element) => {
    if (element.tagName === "SCRIPT" || element.tagName === "STYLE") {
      element.remove();
      return;
    }

    if (!RICH_TEXT_TAGS.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }

    Array.from(element.attributes).forEach((attribute) => {
      const isAlignmentClass = ["ql-align-center", "ql-align-right"].includes(
        attribute.name === "class" ? attribute.value : ""
      );
      const isLinkAttribute =
        element.tagName === "A" && ["href", "target", "rel"].includes(attribute.name);
      if (!isAlignmentClass && !isLinkAttribute) element.removeAttribute(attribute.name);
    });

    if (element.tagName === "A") {
      const href = element.getAttribute("href") ?? "";
      if (!/^(https?:|mailto:)/i.test(href)) element.removeAttribute("href");
      element.setAttribute("target", "_blank");
      element.setAttribute("rel", "noopener noreferrer");
    }
  });

  return parsed.body.innerHTML;
}
